// AddPaymentMethodInline — the shared "add a saved card" UI (SetupIntent → Stripe Elements →
// confirmCardSetup → save MIT row). EXTRACTED verbatim from SavedCardsSection's add-card flow
// (3f 2d-ii Step 2) so the activity-join confirm can offer add-card without duplicating the
// Stripe.js wiring (Lesson 38). Pure move: same useSavedCards.addCard → client_secret →
// elements().create("card") mounted on #stripe-card-element → confirmCardSetup → saveCard →
// player_payment_methods MIT row → query invalidation. Renders the card element + Save button;
// the parent decides where it appears and reacts via onSaved.
//
// NOTE (kept verbatim from the source): the card element mounts on the global DOM id
// `#stripe-card-element` via getElementById after a 100ms tick (lets the dialog render first).
// Only one add-card surface is ever open at a time, so the shared id does not collide.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSavedCards } from "@/hooks/useSavedCards";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { getStripe } from "@/lib/stripe";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AddPaymentMethodInlineProps {
  /** Called after a card is successfully saved (MIT row created + cards query invalidated). */
  onSaved: () => void;
  /** Optional cancel affordance. */
  onCancel?: () => void;
}

export default function AddPaymentMethodInline({ onSaved, onCancel }: AddPaymentMethodInlineProps) {
  const { t } = useTranslation("common");
  const { addCard, saveCard } = useSavedCards();
  const { organization } = useOrganization();
  const [adding, setAdding] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<any>(null);
  const [cardElementInstance, setCardElementInstance] = useState<any>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // On mount: create the SetupIntent + mount the Stripe card element (verbatim from handleOpenAdd).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await addCard.mutateAsync();
        if (cancelled) return;
        setClientSecret(result.client_secret);

        // 2d-ii Step 2b: resolve the org's mode-matched publishable key so Stripe.js matches the
        // SetupIntent's mode (test for demo, live for AP). getStripe falls back to the env key if null.
        let pubKey: string | null = null;
        if (organization?.id) {
          const { data: bc } = await supabase
            .from("billing_config")
            .select("stripe_mode, stripe_publishable_key_test, stripe_publishable_key_live")
            .eq("organization_id", organization.id)
            .maybeSingle();
          if (bc) {
            pubKey = (bc as { stripe_mode?: string }).stripe_mode === "test"
              ? (bc as { stripe_publishable_key_test?: string }).stripe_publishable_key_test ?? null
              : (bc as { stripe_publishable_key_live?: string }).stripe_publishable_key_live ?? null;
          }
        }
        const stripe = await getStripe(pubKey);
        if (cancelled) return;
        if (!stripe) {
          toast.error("Stripe not configured");
          return;
        }
        if (cancelled) return;
        setStripeInstance(stripe);

        // Create and mount card element after the dialog renders.
        setTimeout(() => {
          const elements = stripe.elements();
          const ce = elements.create("card", {
            style: {
              base: { fontSize: "16px", color: "#32325d", "::placeholder": { color: "#aab7c4" } },
            },
          });
          const container = document.getElementById("stripe-card-element");
          if (container) {
            ce.mount(container);
            setCardElementInstance(ce);
          }
        }, 100);
      } catch (err: any) {
        toast.error(err.message || "Could not initialize card form");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveCard = async () => {
    if (!stripeInstance || !cardElementInstance || !clientSecret) return;
    setAdding(true);
    try {
      const { error, setupIntent } = await stripeInstance.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElementInstance },
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      if (setupIntent?.id) {
        await saveCard.mutateAsync(setupIntent.id);
        toast.success(t("profile.payment.cardSaved"));
      }
      setClientSecret(null);
      setCardElementInstance(null);
      onSaved();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-3">
      <div id="stripe-card-element" className="border rounded-md p-3 min-h-[44px]" />
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            {t("cancel")}
          </Button>
        )}
        <Button onClick={handleSaveCard} disabled={adding || !clientSecret}>
          {adding && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {t("profile.payment.saveCard")}
        </Button>
      </div>
    </div>
  );
}
